// src/components/recharge/WindDownCard.jsx
//
// Wind-down routine: the Sleep checklist's advice as a routine you run.
// Steps live in the member's private journal (winddown_routine); "Start"
// hands a guided tool to the ResetTool runner via onStart.

import React, { useEffect, useMemo, useState } from 'react';
import { Moon, Play, Pencil, Check, Plus, X, ArrowUp, ArrowDown, Timer } from 'lucide-react';
import { SUGGESTED_STEPS, DEFAULT_ROUTINE_IDS, stepsFromIds, routineToTool } from '../../data/winddown';
import { subscribeJournalEntry, saveJournalEntry } from '../../utils/journalStore';

const ROUTINE_ID = 'winddown_routine';

const WindDownCard = ({ uid, onStart, lastRun }) => {
    const [saved, setSaved] = useState(null);      // journal doc
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);      // steps while editing
    const [custom, setCustom] = useState('');

    useEffect(() => {
        if (!uid) return undefined;
        const unsub = subscribeJournalEntry(uid, ROUTINE_ID, setSaved);
        return () => unsub();
    }, [uid]);

    const steps = useMemo(() => (Array.isArray(saved?.steps) && saved.steps.length > 0) ? saved.steps : stepsFromIds(DEFAULT_ROUTINE_IDS), [saved]);
    const total = steps.reduce((s, x) => s + (Number(x.minutes) || 0), 0);

    const startEdit = () => { setDraft(steps.map(s => ({ ...s }))); setEditing(true); };
    const persist = async (next) => {
        if (!uid) return;
        try {
            await saveJournalEntry(uid, ROUTINE_ID, { worksheetId: ROUTINE_ID, kind: 'routine', title: 'Wind-down routine', topicId: 'sleep', steps: next, progress: { answered: next.length, total: next.length, pct: 100 } });
        } catch (e) { console.error('routine save failed', e); }
    };
    const done = async () => { await persist(draft); setEditing(false); };
    const move = (i, dir) => setDraft(d => { const n = [...d]; const j = i + dir; if (j < 0 || j >= n.length) return d; [n[i], n[j]] = [n[j], n[i]]; return n; });
    const remove = (i) => setDraft(d => d.filter((_, k) => k !== i));
    const addSuggested = (s) => setDraft(d => d.some(x => x.id === s.id) ? d : [...d, { ...s }]);
    const addCustom = () => { const t = custom.trim(); if (!t) return; setDraft(d => [...d, { id: `custom_${Date.now()}`, title: t, minutes: 5, detail: '' }]); setCustom(''); };
    const setMinutes = (i, v) => setDraft(d => d.map((s, k) => k === i ? { ...s, minutes: Math.max(1, Math.min(60, Number(v) || 1)) } : s));

    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Moon size={22} /></div>
                    <div>
                        <h2 className="text-lg font-bold text-slate-800">Wind-down routine</h2>
                        <p className="text-sm text-slate-500 mt-0.5">The Sleep checklist, run as a routine. Same steps, same order, every night.</p>
                        {lastRun && <p className="text-xs text-slate-400 mt-1">Last run {lastRun}.</p>}
                    </div>
                </div>
                {!editing && (
                    <button onClick={startEdit} className="px-3 py-2 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100 flex items-center gap-1 shrink-0"><Pencil size={13} /> Edit</button>
                )}
            </div>

            {!editing ? (
                <>
                    <div className="flex flex-wrap gap-2 mt-4">
                        {steps.map((s, i) => (
                            <span key={s.id || i} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-full">
                                <span className="w-4 h-4 rounded-full bg-indigo-100 text-indigo-700 text-[10px] flex items-center justify-center font-black">{i + 1}</span>
                                {s.title} <span className="text-slate-400 font-medium">· {s.minutes} min</span>
                            </span>
                        ))}
                    </div>
                    <div className="flex items-center justify-between mt-4">
                        <span className="text-xs font-bold text-slate-400 flex items-center gap-1"><Timer size={12} /> about {total} min</span>
                        <button onClick={() => onStart(routineToTool(steps))} className="px-5 py-2.5 rounded-xl font-bold text-sm bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"><Play size={16} /> Start wind-down</button>
                    </div>
                </>
            ) : (
                <div className="mt-4 space-y-3 animate-in fade-in duration-200">
                    <div className="space-y-2">
                        {draft.map((s, i) => (
                            <div key={s.id || i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-2">
                                <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] flex items-center justify-center font-black shrink-0">{i + 1}</span>
                                <span className="flex-1 text-sm font-bold text-slate-800 truncate">{s.title}</span>
                                <input type="number" min={1} max={60} value={s.minutes} onChange={e => setMinutes(i, e.target.value)} className="w-16 p-1.5 border border-slate-200 rounded-lg text-sm text-center" />
                                <span className="text-[11px] text-slate-400">min</span>
                                <button onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700" title="Up"><ArrowUp size={14} /></button>
                                <button onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700" title="Down"><ArrowDown size={14} /></button>
                                <button onClick={() => remove(i)} className="p-1 text-slate-300 hover:text-red-500" title="Remove"><X size={14} /></button>
                            </div>
                        ))}
                        {draft.length === 0 && <p className="text-xs text-slate-400 px-1">No steps — add some below.</p>}
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">Add a step</p>
                        <div className="flex flex-wrap gap-1.5">
                            {SUGGESTED_STEPS.filter(s => !draft.some(x => x.id === s.id)).map(s => (
                                <button key={s.id} onClick={() => addSuggested(s)} className="px-2.5 py-1.5 rounded-full text-[11px] font-bold bg-white border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700 flex items-center gap-1"><Plus size={11} /> {s.title}</button>
                            ))}
                        </div>
                        <div className="flex gap-2 mt-2">
                            <input value={custom} onChange={e => setCustom(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} placeholder="Your own step…" className="flex-1 p-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-300" />
                            <button onClick={addCustom} className="px-3 py-2 rounded-xl text-sm font-bold bg-slate-100 text-slate-700 hover:bg-slate-200">Add</button>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100">Cancel</button>
                        <button onClick={done} className="px-4 py-2 rounded-xl text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1.5"><Check size={14} strokeWidth={3} /> Save routine</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WindDownCard;
